import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTenant } from '../config/tenantConfig';
import { useDarkMode } from '../DarkModeContext';
import { useAuth } from '../context/AuthContext';
import { useSidePanel } from '../context/SidePanelContext';
import Swal from 'sweetalert2';
import SupportChatAgent from '../components/SupportChatAgent';
import LandingGuideAgent from '../components/LandingGuideAgent';
import SystemStatusIndicator from '../components/SystemStatusIndicator';
import GlassTopBar from '../components/GlassTopBar';
import CollapsibleSidebar from '../components/CollapsibleSidebar';
import {
  Home,
  FileText,
  Users,
  CheckCircle,
  LogOut,
  Menu,
  X,
  Bell,
  Sun,
  Moon,
  Brain,
  MessageSquare,
  Beaker,
  FlaskConical,
  TestTube,
  Target
} from 'lucide-react';

const HALILAB_LOGO = `${process.env.PUBLIC_URL || ''}/assets/halilab-logo-purple.svg`;

/**
 * Layout pour l'application HaliLab
 * Interface moderne pour les outils de prototype et POC
 */
const HaliLabLayout = ({ children }) => {
  const { showSidePanel: isSidebarOpen, setShowSidePanel: setIsSidebarOpen, isMobile } = useSidePanel();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const navigate = useNavigate();
  const { tenantName, theme } = useTenant();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { user: authUser, isAuthenticated, logout } = useAuth();
  const currentUser = useSelector(state => state.userManagement.currentUser);
  const displayUser = currentUser || authUser || null;
  const displayName = displayUser
    ? `${displayUser.firstName || ''} ${displayUser.lastName || ''}`.trim() || displayUser.email
    : '';
  const displayRole = displayUser?.role || displayUser?.category || 'User';
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // On mobile, sidebar overlays — no margin shift needed
  const sidebarWidth = isMobile ? 0 : (isSidebarOpen ? (sidebarCollapsed ? 80 : 256) : 0);
  useEffect(() => {
    if (!isSidebarOpen) {
      setSidebarCollapsed(false);
    }
  }, [isSidebarOpen]);

  // Purple theme for HaliLab - deeper, more vibrant purple
  const haliLabTheme = {
    primary: '#7c3aed', // violet-600
    secondary: '#8b5cf6' // violet-500
  };

  // Menu items for HaliLab users
  const menuItems = [
    {
      icon: Home,
      label: 'Dashboard',
      path: '/halilab/dashboard',
      badge: null
    },
    {
      icon: Beaker,
      label: 'Prototypes',
      path: '/halilab/prototypes',
      badge: null
    },
    {
      icon: FlaskConical,
      label: 'POC Tools',
      path: '/halilab/poc-tools',
      badge: null
    },
    {
      icon: TestTube,
      label: 'Experiments',
      path: '/halilab/experiments',
      badge: null
    },
    {
      icon: MessageSquare,
      label: 'Feedback',
      path: '/halilab/feedback',
      badge: null
    },
    {
      icon: FileText,
      label: 'Documentation',
      path: '/halilab/documentation',
      badge: null
    },
    {
      icon: Target,
      label: 'Leads',
      path: '/halilab/leads',
      badge: null
    }
  ];
  const iconColorMap = {
    Dashboard: '#60a5fa',
    Prototypes: '#a855f7',
    'POC Tools': '#f472b6',
    Experiments: '#22d3ee',
    Feedback: '#facc15',
    Documentation: '#22d3ee',
    Leads: '#ef4444'
  };
  const sidebarMenuItems = menuItems.map((item) => ({
    ...item,
    color: iconColorMap[item.label] || '#7c3aed'
  }));
  const handleSidebarNavigate = (path) => {
    if (!path) return;
    if (location.pathname !== path) {
      navigate(path);
      // Fallback if navigation is blocked by an overlay or stale state
      setTimeout(() => {
        if (window.location.pathname !== path) {
          window.location.href = path;
        }
      }, 50);
    }
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  };

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleBrandClick = () => {
    // Navigate to user's dashboard based on role (don't logout)
    if (!isAuthenticated) {
      navigate('/landing');
      return;
    }
    
    const userRole = (displayUser?.role || displayUser?.category || '').toLowerCase();
    
    if (userRole === 'platform_admin' || userRole === 'platform_user' || userRole === 'platform' || userRole === 'admin') {
      navigate('/platform/dashboard');
    } else if (userRole === 'factor' || userRole === 'funder') {
      navigate('/factor/dashboard');
    } else {
      // Default to customer dashboard
      navigate('/customer/dashboard');
    }
  };

  const handleNotificationsClick = () => {
    setIsChatOpen((prev) => !prev);
  };

  const handleLogout = async () => {
    const { isConfirmed } = await Swal.fire({
      title: 'Confirm logout',
      text: 'Are you sure you want to exit HaliLab?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, exit',
      cancelButtonText: 'Stay',
      reverseButtons: true,
      background: darkMode ? '#1F2937' : '#fff',
      color: darkMode ? '#F9FAFB' : '#111827',
      confirmButtonColor: haliLabTheme.primary
    });

    if (isConfirmed) {
      try {
        await logout();
        navigate('/haliflow-landing');
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <GlassTopBar
        logoSrc={HALILAB_LOGO}
        brandName="HaliLab"
        brandSubtitle="powered by HaliSoft"
        displayRole={displayRole}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        isSidebarCollapsed={!isSidebarOpen}
        onSidebarToggle={handleSidebarToggle}
        onBrandClick={handleBrandClick}
        notifications={0}
        onNotificationsClick={handleNotificationsClick}
        onLogout={handleLogout}
        borderColor={haliLabTheme.primary + '20'}
      />

      <div className="flex">
        {/* Sidebar */}
        <CollapsibleSidebar
          className={!isSidebarOpen ? 'sidebar-hidden' : ''}
          style={{ top: '96px' }}
          logoSrc={HALILAB_LOGO}
          brandName="HaliLab"
          subtitle="Powered by HaliSoft"
          viewBadge="Lab View"
          menuItems={sidebarMenuItems}
          activePath={location.pathname}
          onNavigate={handleSidebarNavigate}
          onToggle={(collapsed) => setSidebarCollapsed(collapsed)}
          primaryColor={haliLabTheme.primary}
          darkMode={darkMode}
        />

        {/* Main Content */}
        <main
          className="flex-1 overflow-auto transition-all duration-300 ease-in-out"
          style={{ marginLeft: sidebarWidth }}
        >
          <div className="p-6 lg:p-8">
            <div className="glass-surface glass-panel rounded-[28px] p-6 lg:p-8 shadow-[0_35px_120px_rgba(15,23,42,0.15)]">
              {children || <Outlet />}
            </div>
          </div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Support Chat Agent - Always mounted to listen for events */}
      <SupportChatAgent onOpenChange={setIsChatOpen} />

      <LandingGuideAgent darkMode={darkMode} theme="default" position="left" />
    </div>
  );
};

export default HaliLabLayout;
